import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Plus, Edit2, Trash2, Save, X, Sparkles, Bug, TrendingUp, Shield, Eye, EyeOff } from 'lucide-react';
import { supabase } from '../../supabaseClient';
import { showNotification, showError } from '../../utils/notificationHandler';
import PageHeader from '../shared/PageHeader';
import Button from '../shared/Button';

const ChangelogManagement = () => {
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isEditing, setIsEditing] = useState(false);
  const [editingEntry, setEditingEntry] = useState(null);
  const [formData, setFormData] = useState({
    version: '',
    title: '',
    description: '',
    category: 'feature',
    is_published: false
  });

  useEffect(() => {
    fetchEntries();
  }, []);

  const fetchEntries = async () => {
    try {
      setLoading(true);
      const { data, error } = await supabase
        .from('app_changelog')
        .select(`
          *,
          accounts:created_by (
            full_name
          )
        `)
        .order('created_at', { ascending: false });

      if (error) throw error;
      setEntries(data || []);
    } catch (error) {
      console.error('Error fetching changelog entries:', error);
      showError(error, 'Failed to load changelog entries');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('Not authenticated');

      if (editingEntry) {
        // Update existing entry
        const { error } = await supabase
          .from('app_changelog')
          .update({
            version: formData.version,
            title: formData.title,
            description: formData.description,
            category: formData.category,
            is_published: formData.is_published
          })
          .eq('id', editingEntry.id);

        if (error) throw error;
        showNotification('Changelog entry updated successfully!', 'success');
      } else {
        // Create new entry
        const { error } = await supabase
          .from('app_changelog')
          .insert([{
            ...formData,
            created_by: user.id
          }]);

        if (error) throw error;
        showNotification('Changelog entry created successfully!', 'success');
      }

      resetForm();
      fetchEntries();
    } catch (error) {
      console.error('Error saving changelog entry:', error);
      showError(error, 'Failed to save changelog entry');
    }
  };

  const handleEdit = (entry) => {
    setEditingEntry(entry);
    setFormData({
      version: entry.version,
      title: entry.title,
      description: entry.description,
      category: entry.category,
      is_published: entry.is_published
    });
    setIsEditing(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this changelog entry?')) {
      return;
    }

    try {
      const { error } = await supabase
        .from('app_changelog')
        .delete()
        .eq('id', id);

      if (error) throw error;
      showNotification('Changelog entry deleted successfully!', 'success');
      fetchEntries();
    } catch (error) {
      console.error('Error deleting changelog entry:', error);
      showError(error, 'Failed to delete changelog entry');
    }
  };

  const resetForm = () => {
    setFormData({
      version: '',
      title: '',
      description: '',
      category: 'feature',
      is_published: false
    });
    setEditingEntry(null);
    setIsEditing(false);
  };

  const getCategoryIcon = (category) => {
    switch (category) {
      case 'feature':
        return <Sparkles className="h-4 w-4" />;
      case 'bugfix':
        return <Bug className="h-4 w-4" />;
      case 'improvement':
        return <TrendingUp className="h-4 w-4" />;
      case 'security':
        return <Shield className="h-4 w-4" />;
      default:
        return <Sparkles className="h-4 w-4" />;
    }
  };

  const getCategoryColor = (category) => {
    switch (category) {
      case 'feature':
        return 'bg-blue-100 text-blue-800 border-blue-200';
      case 'bugfix':
        return 'bg-red-100 text-red-800 border-red-200';
      case 'improvement':
        return 'bg-green-100 text-green-800 border-green-200';
      case 'security':
        return 'bg-orange-100 text-orange-800 border-orange-200';
      default:
        return 'bg-gray-100 text-gray-800 border-gray-200';
    }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <PageHeader
        title="Changelog"
        description="Create and manage application changelog entries"
        actions={!isEditing && (
          <Button
            variant="primary"
            onClick={() => setIsEditing(true)}
            icon={<Plus className="h-5 w-5" />}
          >
            Add Changelog Entry
          </Button>
        )}
      />

      {/* Form */}
      {isEditing && (
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-white border border-gray-200 rounded-lg p-6 shadow-sm"
        >
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-base font-semibold text-gray-900">
              {editingEntry ? 'Edit Changelog Entry' : 'New Changelog Entry'}
            </h3>
            <button
              onClick={resetForm}
              className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
            >
              <X className="h-5 w-5 text-gray-500" />
            </button>
          </div>

          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Version *
                </label>
                <input
                  type="text"
                  value={formData.version}
                  onChange={(e) => setFormData({ ...formData, version: e.target.value })}
                  placeholder="e.g., v1.0.0"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand focus:border-transparent"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Category *
                </label>
                <select
                  value={formData.category}
                  onChange={(e) => setFormData({ ...formData, category: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand focus:border-transparent"
                  required
                >
                  <option value="feature">New Feature</option>
                  <option value="improvement">Improvement</option>
                  <option value="bugfix">Bug Fix</option>
                  <option value="security">Security</option>
                </select>
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Title *
              </label>
              <input
                type="text"
                value={formData.title}
                onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                placeholder="e.g., New Dashboard Analytics Widget"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand focus:border-transparent"
                required
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Description *
              </label>
              <textarea
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                placeholder="Detailed description of the changes..."
                rows={4}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand focus:border-transparent"
                required
              />
            </div>

            <div className="flex items-center">
              <input
                type="checkbox"
                id="is_published"
                checked={formData.is_published}
                onChange={(e) => setFormData({ ...formData, is_published: e.target.checked })}
                className="h-4 w-4 text-brand focus:ring-brand border-gray-300 rounded"
              />
              <label htmlFor="is_published" className="ml-2 block text-sm text-gray-900">
                Publish immediately (users will see this entry)
              </label>
            </div>

            <div className="flex space-x-3 pt-4">
              <button
                type="submit"
                className="inline-flex items-center px-4 py-2 bg-brand hover:bg-brand-hover text-black font-semibold rounded-lg shadow transition-colors"
              >
                <Save className="h-4 w-4 mr-2" />
                {editingEntry ? 'Update Entry' : 'Create Entry'}
              </button>
              <button
                type="button"
                onClick={resetForm}
                className="inline-flex items-center px-4 py-2 bg-gray-200 hover:bg-gray-300 text-gray-700 font-semibold rounded-lg transition-colors"
              >
                Cancel
              </button>
            </div>
          </form>
        </motion.div>
      )}

      {/* Entries List */}
      <div className="bg-white border border-gray-200 rounded-lg shadow-sm">
        <div className="px-6 py-4 border-b border-gray-200">
          <h3 className="text-base font-semibold text-gray-900">
            All Changelog Entries ({entries.length})
          </h3>
        </div>

        <div className="divide-y divide-gray-200">
          {loading ? (
            <div className="px-6 py-8 text-center">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-brand mx-auto"></div>
              <p className="mt-2 text-sm text-gray-500">Loading...</p>
            </div>
          ) : entries.length === 0 ? (
            <div className="px-6 py-12 text-center">
              <Sparkles className="h-12 w-12 text-gray-300 mx-auto mb-4" />
              <p className="text-gray-500">No changelog entries yet</p>
              <p className="text-sm text-gray-400 mt-2">
                Click "Add Changelog Entry" to create your first entry
              </p>
            </div>
          ) : (
            entries.map((entry) => (
              <motion.div
                key={entry.id}
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                className="px-6 py-4 hover:bg-gray-50 transition-colors"
              >
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <div className="flex items-center space-x-2 mb-2">
                      <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium border ${getCategoryColor(entry.category)}`}>
                        {getCategoryIcon(entry.category)}
                        <span className="ml-1">{entry.category}</span>
                      </span>
                      <span className="px-2 py-1 bg-gray-100 text-gray-700 text-xs font-mono rounded">
                        {entry.version}
                      </span>
                      {entry.is_published ? (
                        <span className="inline-flex items-center px-2 py-1 bg-green-100 text-green-800 text-xs font-medium rounded-full">
                          <Eye className="h-3 w-3 mr-1" />
                          Published
                        </span>
                      ) : (
                        <span className="inline-flex items-center px-2 py-1 bg-gray-100 text-gray-600 text-xs font-medium rounded-full">
                          <EyeOff className="h-3 w-3 mr-1" />
                          Draft
                        </span>
                      )}
                    </div>
                    <h4 className="text-base font-semibold text-gray-900 mb-1">
                      {entry.title}
                    </h4>
                    <p className="text-sm text-gray-600 mb-2">
                      {entry.description.substring(0, 150)}
                      {entry.description.length > 150 ? '...' : ''}
                    </p>
                    <div className="flex items-center text-xs text-gray-500 space-x-4">
                      <span>
                        Created: {new Date(entry.created_at).toLocaleDateString()}
                      </span>
                      {entry.published_date && (
                        <span>
                          Published: {new Date(entry.published_date).toLocaleDateString()}
                        </span>
                      )}
                      {entry.accounts && (
                        <span>By: {entry.accounts.full_name}</span>
                      )}
                    </div>
                  </div>

                  <div className="flex items-center space-x-2 ml-4">
                    <button
                      onClick={() => handleEdit(entry)}
                      className="p-2 text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
                      title="Edit"
                    >
                      <Edit2 className="h-4 w-4" />
                    </button>
                    <button
                      onClick={() => handleDelete(entry.id)}
                      className="p-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                      title="Delete"
                    >
                      <Trash2 className="h-4 w-4" />
                    </button>
                  </div>
                </div>
              </motion.div>
            ))
          )}
        </div>
      </div>
    </div>
  );
};

export default ChangelogManagement;
